import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Spinner } from 'react-bootstrap';
import ComprobanteTicket from '../components/ComprobanteTicket';

const PAYPHONE_BACKEND       = 'https://nuevo-payphonesisnetel.u5r75b.easypanel.host';
const N8N_DETALLE_FACTURA    = process.env.REACT_APP_N8N_URL_DETALLE_FACTURA!;
const N8N_COMPROBANTE        = process.env.REACT_APP_N8N_URL_COMPROBANTE!;

const ConfirmacionPago: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params   = new URLSearchParams(location.search);

  const id         = params.get('id');
  const clientTxId = params.get('clientTransactionId');
  const cedula     = params.get('cedula')     ?? '';
  const montoParam = params.get('monto')      ?? '0';
  const nombre     = params.get('nombre')     ?? '';
  const monto      = parseFloat(montoParam);

  const [status, setStatus]   = useState<'loading' | 'approved' | 'error'>('loading');
  const [ppData,  setPpData]  = useState<any>(null);

  // Comprobante SVG
  const [loadingComp,  setLoadingComp]  = useState(false);
  const [svgBase64,    setSvgBase64]    = useState('');
  const [facturaId,    setFacturaId]    = useState<string | number>('');
  const [errorComp,    setErrorComp]    = useState('');
  const [showTicket,   setShowTicket]   = useState(false);

  const generando = useRef(false);

  // ── Verificar pago con backend ─────────────────────────────────────────────
  useEffect(() => {
    if (!id || !clientTxId) { setStatus('error'); return; }

    fetch(`${PAYPHONE_BACKEND}/api/confirmar`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: Number(id), clientTxId }),
    })
      .then(r => r.json())
      .then(data => {
        const aprobado = data.statusCode === 3 || data.transactionStatus === 'Approved';
        if (aprobado) {
          setPpData(data);
          setStatus('approved');
          // Generar comprobante en segundo plano si N8N devolvió facturas_id
          if (data.facturas_id) generarComprobante(data.facturas_id, data);
        } else {
          setStatus('error');
        }
      })
      .catch(() => setStatus('error'));
  }, []);

  // ── Generar comprobante SVG ────────────────────────────────────────────────
  const generarComprobante = async (facturas_id: string | number, payData: any) => {
    if (generando.current) return;
    generando.current = true;
    setLoadingComp(true);
    setErrorComp('');

    try {
      // Paso 1: detalle de la factura
      const resDetalle = await fetch(N8N_DETALLE_FACTURA, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ facturas_id: String(facturas_id) }),
      });
      const rawDetalle = await resDetalle.json();
      const detalle    = Array.isArray(rawDetalle) ? rawDetalle[0] : rawDetalle;

      if (detalle?.estado !== 'exito') throw new Error('Sin detalle');

      setFacturaId(detalle.factura?.id ?? facturas_id);

      const fac   = detalle.factura;
      const items: any[] = detalle.items || [];
      const item  = items.find((i: any) => parseFloat(i.total) > 0) || items[0];
      const lines: string[] = (item?.descrp || '').split('\n').map((l: string) => l.trim()).filter(Boolean);

      const clientData = (() => {
        try { return JSON.parse(localStorage.getItem('clientData') || '{}'); } catch { return {}; }
      })();

      const now  = new Date();
      const p2   = (n: number) => String(n).padStart(2, '0');
      const fechaImpresion = `${p2(now.getDate())}/${p2(now.getMonth()+1)}/${now.getFullYear()} ${p2(now.getHours())}:${p2(now.getMinutes())}:${p2(now.getSeconds())}`;
      const fmtDate = (d: string) => {
        if (!d) return '';
        const pts = d.split('-');
        return pts.length === 3 ? `${pts[2]}/${pts[1]}/${pts[0]}` : d;
      };

      // Paso 2: construir payload para comprobante
      const payload = {
        clienteNombre:      nombre || fac?.nombre || '',
        clienteCedula:      cedula,
        clienteCelular:     clientData?.movil   || '',
        clienteCorreo:      clientData?.correo  || '',
        facturaId:          String(fac.id).padStart(8, '0'),
        planNombre:         lines[0] || '',
        facturacion:        (lines[2] || '').replace('facturación del ', ''),
        suspension:         (lines[3] || '').replace('Fecha de Suspensión : ', ''),
        fechaPago:          fmtDate(fac.fechapago),
        vencimiento:        fmtDate(fac.vencimiento),
        subtotal:           parseFloat(fac.subtotal  || '0').toFixed(2),
        iva:                parseFloat(fac.impuesto  || '0').toFixed(2),
        total:              parseFloat(fac.total     || '0').toFixed(2),
        saldo:              parseFloat(fac.saldo     || '0').toFixed(2),
        pasarela:           'Payphone',
        numeroComprobante:  String(payData.transactionId || id),
        autorizacion:       payData.authorizationCode || '',
        montoDepositado:    monto > 0 ? monto.toFixed(2) : parseFloat(fac.total || '0').toFixed(2),
        fechaImpresion,
      };

      // Paso 3: pedir SVG a N8N
      const resComp  = await fetch(N8N_COMPROBANTE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const compData = await resComp.json();

      if (compData?.base64) {
        setSvgBase64(compData.base64);
      } else {
        setErrorComp('No se pudo generar el comprobante.');
      }
    } catch {
      setErrorComp('No se pudo generar el comprobante.');
    } finally {
      setLoadingComp(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  if (status === 'loading') {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <Spinner animation="border" style={{ color: 'var(--sn-marca)' }} />
        <p className="mt-3" style={{ color: 'var(--sn-texto-2)' }}>Confirmando pago...</p>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="container py-4 text-center">
        <div style={{ fontSize: 64 }}>⚠️</div>
        <h4 className="mt-3" style={{ color: 'var(--sn-texto)' }}>Error al confirmar el pago</h4>
        <p style={{ color: 'var(--sn-texto-2)' }}>Revisa tu conexión o contacta a soporte.</p>
        <Button
          variant="primary"
          onClick={() => navigate('/menu')}
          style={{ background: 'var(--sn-accion)', borderColor: 'var(--sn-accion)', color: 'var(--sn-accion-texto)' }}
        >
          Ir al inicio
        </Button>
      </div>
    );
  }

  // Pantalla de éxito
  const fecha = new Date();
  const p2 = (n: number) => String(n).padStart(2, '0');
  const fechaStr = `${p2(fecha.getDate())}/${p2(fecha.getMonth()+1)}/${fecha.getFullYear()} ${p2(fecha.getHours())}:${p2(fecha.getMinutes())}`;

  if (showTicket && svgBase64) {
    return (
      <div className="container py-4" style={{ maxWidth: 480 }}>
        <ComprobanteTicket
          svgBase64={svgBase64}
          facturaId={facturaId}
          onClose={() => setShowTicket(false)}
        />
      </div>
    );
  }

  return (
    <div className="container py-4 d-flex flex-column align-items-center" style={{ maxWidth: 480 }}>

      {/* Encabezado */}
      <div style={{ fontSize: 72, lineHeight: 1 }}>✅</div>
      <h3 className="fw-bold mt-3 mb-1" style={{ color: 'var(--sn-exito)' }}>¡Pago aprobado!</h3>
      <p className="mb-4" style={{ fontSize: '0.9rem', color: 'var(--sn-texto-2)' }}>Tu servicio será activado en breve.</p>

      {/* Tarjeta de datos */}
      <div
        className="w-100 rounded-4 shadow-sm border p-3 mb-4"
        style={{ background: 'var(--sn-superficie-2)', borderColor: 'var(--sn-borde)', boxShadow: 'var(--sn-sombra)' }}
      >
        <div
          className="mb-2 pb-2 border-bottom d-flex justify-content-between align-items-center"
          style={{ borderBottomColor: 'var(--sn-borde)' }}
        >
          <span className="fw-bold" style={{ fontSize: '1rem', color: 'var(--sn-marca)' }}>🌐 Sisnetel Fiber</span>
          <span className="badge" style={{ background: 'var(--sn-exito)', color: 'var(--sn-texto-invertido)' }}>Aprobado</span>
        </div>

        <Fila label="👤 Cliente"       valor={nombre  || '—'} />
        <Fila label="🪪 Cédula"        valor={cedula  || '—'} />
        <Fila label="💰 Monto pagado"  valor={monto > 0 ? `$${monto.toFixed(2)}` : '—'} bold />
        <Fila label="🆔 Transacción"   valor={ppData?.transactionId ? String(ppData.transactionId) : (id ?? '—')} />
        {ppData?.authorizationCode && (
          <Fila label="🔐 Autorización" valor={ppData.authorizationCode} />
        )}
        <Fila label="📅 Fecha"         valor={fechaStr} />
        <Fila label="💳 Pasarela"      valor="Payphone" />
      </div>

      {/* Botón comprobante */}
      {loadingComp && (
        <div className="text-center mb-3" style={{ color: 'var(--sn-texto-2)' }}>
          <Spinner animation="border" size="sm" className="me-2" />
          <span className="small">Generando comprobante...</span>
        </div>
      )}

      {errorComp && (
        <p className="small mb-3" style={{ color: 'var(--sn-alerta)' }}>{errorComp}</p>
      )}

      <Button
        variant="primary"
        size="lg"
        className="w-100 mb-2"
        disabled={loadingComp || (!svgBase64 && !errorComp)}
        onClick={() => { if (svgBase64) setShowTicket(true); }}
        style={{ background: 'var(--sn-accion)', borderColor: 'var(--sn-accion)', color: 'var(--sn-accion-texto)' }}
      >
        {loadingComp ? 'Preparando comprobante...' : '🧾 Ver comprobante'}
      </Button>

      <Button
        variant="outline-secondary"
        className="w-100"
        onClick={() => navigate('/menu')}
        style={{ background: 'transparent', borderColor: 'var(--sn-borde-fuerte)', color: 'var(--sn-texto-2)' }}
      >
        Ir al inicio
      </Button>
    </div>
  );
};

// Componente auxiliar para filas de la tarjeta
const Fila: React.FC<{ label: string; valor: string; bold?: boolean }> = ({ label, valor, bold }) => (
  <div className="d-flex justify-content-between align-items-center py-1" style={{ fontSize: '0.88rem' }}>
    <span style={{ color: 'var(--sn-texto-2)' }}>{label}</span>
    <span
      className={bold ? 'fw-bold fs-6' : 'fw-semibold'}
      style={{ color: bold ? 'var(--sn-exito)' : 'var(--sn-texto)' }}
    >
      {valor}
    </span>
  </div>
);

export default ConfirmacionPago;
